import './styles.scss'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Avatar, ClickAwayListener, Popper } from '@mui/material'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import CampaignIcon from '@mui/icons-material/Campaign'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { styled } from '@mui/system'
import { colorPalette } from '../../../../style/colorPalette'
import {
  MentionEntityAttributeKey,
  MentionSuggestionData,
  TeamMentionSuggestionData,
} from '../lib/Converter'
import { MentionType } from '../../../../../store/comments'
import { TeamEntity } from '../../../../../domain/entity/TeamEntity'

const StyledPeopleAltIcon = styled(PeopleAltIcon)({
  color: colorPalette.monotone[3],
})
const StyledCampaignIcon = styled(CampaignIcon)({
  color: colorPalette.monotone[3],
})
const StyledChevronRightIcon = styled(ChevronRightIcon)({
  color: colorPalette.monotone[3],
  fontSize: '16px',
  marginLeft: 'auto',
  marginRight: '-8px',
})
const StyledDivider = styled('hr')({
  border: 'none',
  borderTop: `1px solid ${colorPalette.monotone[2]}`,
  margin: '4px 0 0',
})

interface MentionsProps {
  items: MentionSuggestionData[]
  command: (data: Record<string, string | undefined>) => void
}

export default forwardRef((props: MentionsProps, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isTeamPopperOpen, setIsTeamPopperOpen] = useState(false)
  const [focusedTeamIndex, setFocusedTeamIndex] = useState(0)
  const [teams, setTeams] = useState<TeamEntity[]>([])

  const teamItemRef = useRef<HTMLDivElement | null>(null)
  const selectedItemRef = useRef<HTMLDivElement | null>(null)
  const focusedTeamRef = useRef<HTMLDivElement>(null)

  const openTeamPopper = useCallback((teams: TeamEntity[]) => {
    setTeams(teams)
    setFocusedTeamIndex(0)
    setIsTeamPopperOpen(true)
  }, [])

  const closeTeamPopper = useCallback(() => {
    setIsTeamPopperOpen(false)
    setFocusedTeamIndex(0)
  }, [])

  const propsRef = useRef(props)
  propsRef.current = props

  const selectTeam = useCallback((team: TeamEntity | undefined) => {
    if (!team) return
    propsRef.current.command({
      id: team.uuid,
      label: team.displayName,
      [MentionEntityAttributeKey.mentionUuid]: team.uuid,
      [MentionEntityAttributeKey.mentionType]: MentionType.TEAM_MEMBER,
    })
  }, [])

  const selectItem = useCallback(
    (index: number) => {
      const item = propsRef.current.items[index]
      if (!item) return
      if (item.mentionType === MentionType.TEAM_MEMBER) {
        openTeamPopper(item.teams)
        return
      }
      propsRef.current.command({
        id: item.uuid,
        label: item.name,
        [MentionEntityAttributeKey.mentionUuid]: item.uuid,
        [MentionEntityAttributeKey.mentionType]: item.mentionType,
      })
    },
    [openTeamPopper]
  )

  const upHandler = useCallback(() => {
    if (isTeamPopperOpen) {
      setFocusedTeamIndex(prev => Math.max(0, prev - 1))
      return
    }
    setSelectedIndex(prev => Math.max(0, prev - 1))
  }, [isTeamPopperOpen])

  const downHandler = useCallback(() => {
    if (isTeamPopperOpen) {
      setFocusedTeamIndex(prev => Math.min(teams.length - 1, prev + 1))
      return
    }
    setSelectedIndex(prev =>
      Math.min(propsRef.current.items.length - 1, prev + 1)
    )
  }, [isTeamPopperOpen, teams])

  const enterHandler = useCallback(() => {
    if (isTeamPopperOpen) {
      selectTeam(teams[focusedTeamIndex])
      return
    }
    selectItem(selectedIndex)
  }, [
    isTeamPopperOpen,
    teams,
    focusedTeamIndex,
    selectedIndex,
    selectItem,
    selectTeam,
  ])

  useEffect(() => {
    setSelectedIndex(0)

    const isOnlyTeamOption =
      props.items.length === 1 &&
      props.items[0].mentionType === MentionType.TEAM_MEMBER

    if (!isOnlyTeamOption) {
      closeTeamPopper()
      return
    }

    // チーム候補のみの場合、チームポッパーを自動展開する
    const firstItem = props.items[0] as TeamMentionSuggestionData
    openTeamPopper(firstItem.teams ?? [])
  }, [props.items, closeTeamPopper, openTeamPopper])

  useEffect(() => {
    if (selectedItemRef?.current) {
      selectedItemRef.current.scrollIntoView({
        block: 'nearest',
      })
    }
  }, [selectedIndex])

  useEffect(() => {
    if (focusedTeamRef?.current) {
      focusedTeamRef.current.scrollIntoView({
        block: 'nearest',
      })
    }
  }, [focusedTeamIndex])

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          upHandler()
          return true
        }
        if (event.key === 'ArrowDown') {
          downHandler()
          return true
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          enterHandler()
          return true
        }
        if (
          (event.key === 'Escape' || event.key === 'ArrowLeft') &&
          isTeamPopperOpen
        ) {
          closeTeamPopper()
          return true
        }
        return false
      },
    }),
    [upHandler, downHandler, enterHandler, isTeamPopperOpen, closeTeamPopper]
  )
  return (
    <div className="items" onScroll={closeTeamPopper}>
      {props.items.length ? (
        props.items.map((item, index) => {
          const isGroupMention =
            item.mentionType === MentionType.TEAM_MEMBER ||
            item.mentionType === MentionType.PROJECT_MEMBER
          const nextItem = props.items[index + 1]
          const showDivider =
            isGroupMention &&
            nextItem &&
            nextItem.mentionType === MentionType.USER
          return (
            <div key={'mention' + index}>
              <div
                ref={(el: HTMLDivElement | null) => {
                  if (item.mentionType === MentionType.TEAM_MEMBER) {
                    teamItemRef.current = el
                  }
                  if (selectedIndex === index) {
                    selectedItemRef.current = el
                  }
                }}
                className={`item${selectedIndex === index ? ' focused' : ''}`}
                onMouseEnter={() =>
                  !isTeamPopperOpen && setSelectedIndex(index)
                }
                onClick={() => !isTeamPopperOpen && selectItem(index)}
              >
                {item.mentionType === MentionType.TEAM_MEMBER ? (
                  <StyledPeopleAltIcon className={'icon'} />
                ) : item.mentionType === MentionType.PROJECT_MEMBER ? (
                  <StyledCampaignIcon className={'icon'} />
                ) : (
                  <Avatar
                    variant="circular"
                    className={'icon'}
                    src={item.avatar}
                  />
                )}
                <span className={'textName'}>{item.name}</span>
                {item.mentionType === MentionType.TEAM_MEMBER && (
                  <StyledChevronRightIcon />
                )}
              </div>
              {showDivider && <StyledDivider />}
            </div>
          )
        })
      ) : (
        <div className="item">No result</div>
      )}
      <Popper
        open={isTeamPopperOpen}
        anchorEl={teamItemRef.current}
        placement="right-start"
        className="teamPopper"
      >
        <ClickAwayListener
          onClickAway={closeTeamPopper}
          mouseEvent="onMouseDown"
        >
          <div className="teamPopperContent">
            {teams.map((team, index) => (
              <div
                key={team.uuid}
                ref={focusedTeamIndex === index ? focusedTeamRef : null}
                className={`item${focusedTeamIndex === index ? ' focused' : ''}`}
                onMouseEnter={() => setFocusedTeamIndex(index)}
                onClick={() => selectTeam(team)}
              >
                <Avatar
                  variant="circular"
                  className={'icon'}
                  src={team.iconUrl}
                />
                <span className={'textName'}>{team.displayName}</span>
              </div>
            ))}
          </div>
        </ClickAwayListener>
      </Popper>
    </div>
  )
})
